import { AuthContext } from '@core/shared';
import { OptionsApi, WebinarDataWithStatus } from '@core/swagger-client';
import { ErrorMessage } from '@core/ui';
import { Link } from '@online-webinar/ui-frontend';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { WebinarsListItem } from '../../molecules';

export const WebinarsListOnDemand = ({ topN, interestedOnly, search }: { topN?: number; interestedOnly?: boolean; search?: string }) => {
    const { t } = useTranslation();
    const isAuthenticated = useContext(AuthContext).isAuthenticated;

    const { data: webinars, error: error } = useSuspenseQuery(
        OptionsApi.webinars.getOnDemand({
            refetchInterval: 60_000,
            staleTime: 60_000
        })
    );

    if (error) {
        return <ErrorMessage apiError={error} />;
    }

    const webinarFilter = (webinar: WebinarDataWithStatus) => {
        if (interestedOnly && !webinar.isInterested) {
            return false;
        }
        if (
            search &&
            !webinar.title.toLowerCase().includes(search.toLowerCase()) &&
            !webinar.description.toLowerCase().includes(search.toLowerCase()) &&
            !webinar.tags.some((tag) => tag.toLowerCase().includes(search.toLowerCase())) &&
            !webinar.nameOfHost.toLowerCase().includes(search.toLowerCase())
        ) {
            return false;
        }
        return true;
    };

    if (!webinars || webinars.filter((webinar) => webinarFilter(webinar)).length === 0) {
        if (isAuthenticated) {
            return (
                <>
                    <p className="text-2xl mb-2">{t('Schedule.NoWebinars.Title')}</p>
                    <p className="mb-10">{t('Schedule.NoWebinars.Text')}</p>
                </>
            );
        }
        return <p className="mt-6 mb-10">{t('Schedule.NoPublicWebinars.Text')}</p>;
    }

    return (
        <>
            <div className="grid gap-4 md:gap-6 mt-6 md:mt-8 -mx-2 xs:mx-0">
                {webinars
                    .slice(0, topN)
                    .filter((webinar) => webinarFilter(webinar))
                    .map((webinar) => (
                        <WebinarsListItem isAuthenticated={isAuthenticated} key={webinar.webinarId} webinar={webinar} />
                    ))}
            </div>
            {topN && webinars.filter((webinar) => webinarFilter(webinar)).length > topN && (
                <Link to="/on-demand" variant="primary" classNames="mt-6">
                    {t('Schedule.ShowMoreOndemand')}
                </Link>
            )}
        </>
    );
};
